import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Brain, Warning, Target, Repeat, Lightning, ChartLine, Scales, Heart, ArrowRight, CheckCircle, XCircle, BookOpen, Gear } from "phosphor-react";
import { updateSeoMetadata, getArticleAlternates } from "../../lib/seo";

const PorQueConsistenciaEDificil = () => {
  useEffect(() => {
    updateSeoMetadata({
      title: 'Consistência no Trading | Por Que é Difícil e Como Melhorar',
      description: 'Entenda por que consistência é difícil no trading e como melhorar com disciplina, psicologia e análise de dados.',
      path: '/pt/por-que-consistencia-e-dificil-no-trading',
      lang: 'pt-BR',
      alternates: getArticleAlternates('/pt/por-que-consistencia-e-dificil-no-trading'),
    });
    
    return () => {
      document.title = 'GENI — Turn trading data into better decisions';
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header */}
      <header className="border-b border-gray-800/50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/geni-logo.png" alt="GENI" className="h-8" />
            <span className="text-xl font-bold">GENI</span>
          </Link>
          <Link
            to="/signup"
            className="bg-[#00FF85] text-black px-5 py-2 rounded-lg font-semibold hover:bg-[#00DD70] transition-colors"
          >
            Começar Grátis
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        {/* Breadcrumb */}
        <nav className="text-sm text-gray-500 mb-8">
          <Link to="/" className="hover:text-[#00FF85]">Home</Link>
          <span className="mx-2">/</span>
          <span className="text-gray-400">Consistência no Trading</span>
        </nav>

        {/* Category Tag */}
        <div className="flex items-center gap-2 text-amber-400 text-sm font-medium mb-4">
          <Scales size={16} weight="bold" />
          <span>Disciplina & Consistência</span>
        </div>

        {/* H1 */}
        <h1 className="text-4xl sm:text-5xl font-bold mb-4 leading-tight">
          Por Que Consistência é Tão Difícil no Trading
        </h1>

        <p className="text-xl text-gray-400 mb-12">
          (E Como Melhorar)
        </p>

        {/* Introdução */}
        <div className="prose prose-invert max-w-none">
          <p className="text-lg text-gray-300 leading-relaxed mb-6">
            Você sabe o que fazer. Conhece as regras. Estudou os setups. Mas na hora de executar, algo acontece. Você quebra as regras, entra fora do plano, move o stop, fecha cedo demais. E depois se pergunta: <em className="text-white">"Por que eu fiz isso?"</em>
          </p>

          <p className="text-lg text-gray-300 leading-relaxed mb-12">
            Esse é o paradoxo da consistência no trading: <strong className="text-white">saber o que fazer não significa conseguir fazer.</strong> E entender por que isso acontece é o primeiro passo para mudar.
          </p>

          {/* Seção 1 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Lightning size={24} className="text-amber-400" />
              </div>
              <h2 className="text-2xl font-bold">Saber Não é o Mesmo Que Fazer</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Existe uma diferença brutal entre conhecimento e execução. Você pode saber que deveria esperar a confirmação antes de entrar. Você pode saber que não deveria mover o stop. Você pode saber que revenge trading destrói contas.
            </p>

            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-6 mb-6">
              <p className="text-gray-300 leading-relaxed mb-4">
                <strong className="text-amber-400">Conhecimento ≠ Execução</strong>
              </p>
              <p className="text-gray-400 text-sm">
                Traders sabem as regras, mas não conseguem segui-las. Isso não é ignorância — é um problema de comportamento, emoção e estrutura mental. A solução não está em estudar mais, mas em treinar a execução.
              </p>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Pense em alguém que sabe que deveria fazer exercício, comer melhor, dormir mais. Saber não muda o comportamento automaticamente. No trading é igual: conhecimento é necessário, mas insuficiente.
            </p>
          </section>

          {/* Seção 2 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <Heart size={24} className="text-red-400" />
              </div>
              <h2 className="text-2xl font-bold">Emoções no Trading</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              O mercado é um gatilho emocional. Cada movimento de preço ativa reações no seu cérebro que você muitas vezes nem percebe. E essas emoções tomam o controle antes que a razão tenha chance de intervir.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center">
                    <span className="text-blue-400 text-lg">😰</span>
                  </div>
                  <h3 className="font-semibold text-white">Medo</h3>
                </div>
                <p className="text-gray-400 text-sm">Faz você hesitar, sair cedo, evitar entradas válidas. O medo de perder paralisa ou precipita decisões ruins.</p>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center">
                    <span className="text-green-400 text-lg">🤑</span>
                  </div>
                  <h3 className="font-semibold text-white">Ganância</h3>
                </div>
                <p className="text-gray-400 text-sm">Faz você segurar trades além do alvo, aumentar posição irracionalmente, ignorar sinais de saída. Quer sempre mais.</p>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-full bg-purple-500/10 flex items-center justify-center">
                    <span className="text-purple-400 text-lg">😓</span>
                  </div>
                  <h3 className="font-semibold text-white">Ansiedade</h3>
                </div>
                <p className="text-gray-400 text-sm">Gera pressa, impulsividade, necessidade de estar sempre posicionado. A ansiedade odeia esperar — e trading exige paciência.</p>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-full bg-amber-500/10 flex items-center justify-center">
                    <span className="text-amber-400 text-lg">😎</span>
                  </div>
                  <h3 className="font-semibold text-white">Overconfidence</h3>
                </div>
                <p className="text-gray-400 text-sm">Após uma sequência de ganhos, você acha que "entendeu o mercado". Aumenta risco, relaxa regras. E leva a porrada.</p>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Emoções são parte de ser humano. O objetivo não é eliminá-las, mas reconhecê-las e não deixá-las dirigir suas decisões.
            </p>
          </section>

          {/* Seção 3 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Brain size={24} className="text-purple-400" />
              </div>
              <h2 className="text-2xl font-bold">Como o Cérebro Influencia Decisões</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Nosso cérebro não foi projetado para trading. Ele evoluiu para sobreviver em um ambiente completamente diferente. E isso cria problemas sérios quando tentamos tomar decisões financeiras racionais.
            </p>

            <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-6 mb-6">
              <div className="flex items-start gap-4 mb-4">
                <BookOpen size={28} className="text-purple-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-white mb-2">"Rápido e Devagar: Duas Formas de Pensar" — Daniel Kahneman</h3>
                  <p className="text-gray-400 text-sm">Kahneman, Nobel de Economia, explica como a mente funciona em dois sistemas:</p>
                </div>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-4">
                  <h4 className="font-semibold text-red-400 mb-2">Sistema 1: Rápido</h4>
                  <ul className="text-gray-400 text-sm space-y-1">
                    <li>• Automático e inconsciente</li>
                    <li>• Emocional e intuitivo</li>
                    <li>• Reage instantaneamente</li>
                    <li>• Busca atalhos mentais</li>
                  </ul>
                </div>
                <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-lg p-4">
                  <h4 className="font-semibold text-[#00FF85] mb-2">Sistema 2: Lento</h4>
                  <ul className="text-gray-400 text-sm space-y-1">
                    <li>• Deliberado e consciente</li>
                    <li>• Lógico e analítico</li>
                    <li>• Exige esforço mental</li>
                    <li>• Avalia com cuidado</li>
                  </ul>
                </div>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              O problema: no trading, o Sistema 1 quer assumir o controle. Ele vê o preço caindo e grita "VENDE!" antes que o Sistema 2 tenha chance de analisar. Consistência exige treinar o Sistema 2 para intervir.
            </p>
          </section>

          {/* Seção 4 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <Warning size={24} className="text-red-400" />
              </div>
              <h2 className="text-2xl font-bold">Por Que Traders Quebram Regras</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Todo trader tem regras. Poucos as seguem consistentemente. Por quê?
            </p>

            <div className="space-y-4 mb-6">
              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-red-400 mb-2">Impulsividade</h3>
                <p className="text-gray-400 text-sm">O mercado se move, você sente a urgência de agir. A impulsividade é o inimigo número um da consistência. Ela não pede permissão — ela age.</p>
              </div>

              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-red-400 mb-2">Reação ao Mercado</h3>
                <p className="text-gray-400 text-sm">Em vez de seguir o plano, o trader reage ao que o mercado está fazendo. Vê uma vela grande, entra. Vê volatilidade, sai. Vira refém do gráfico.</p>
              </div>

              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-red-400 mb-2">Falta de Disciplina</h3>
                <p className="text-gray-400 text-sm">Disciplina não é natural — é construída. Sem prática deliberada, sem consequências claras, a disciplina não se sustenta sob pressão.</p>
              </div>
            </div>

            <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <BookOpen size={28} className="text-[#00FF85] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-white mb-2">"Trading in the Zone" — Mark Douglas</h3>
                  <p className="text-gray-400 text-sm mb-3">
                    Douglas introduz o conceito de <strong className="text-white">pensamento probabilístico</strong>: entender que cada trade é único e independente, e que você não controla o resultado — apenas a execução.
                  </p>
                  <p className="text-gray-500 text-xs">Quando você internaliza que perdas são parte do jogo, para de reagir emocionalmente a cada resultado e consegue seguir as regras.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Seção 5 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Scales size={24} className="text-amber-400" />
              </div>
              <h2 className="text-2xl font-bold">Feedback Inconsistente</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Um dos aspectos mais traiçoeiros do trading é que o feedback do mercado é inconsistente. E isso confunde profundamente o cérebro.
            </p>

            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-6 mb-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-amber-400 mb-3">Trades bons podem dar loss</h3>
                  <p className="text-gray-400 text-sm">Você seguiu todas as regras, executou perfeitamente, e perdeu. Isso é normal — probabilidade não garante resultado individual. Mas o cérebro interpreta como "errado".</p>
                </div>
                <div>
                  <h3 className="font-semibold text-amber-400 mb-3">Trades ruins podem dar win</h3>
                  <p className="text-gray-400 text-sm">Você entrou por impulso, sem setup, e ganhou. O cérebro interpreta como "certo" e reforça o comportamento errado. Você é recompensado por quebrar regras.</p>
                </div>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Esse feedback inconsistente cria confusão. O cérebro não consegue distinguir o que realmente funciona porque o resultado de curto prazo não reflete a qualidade da decisão. Por isso análise de longo prazo é tão importante.
            </p>
          </section>

          {/* Seção 6 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <ChartLine size={24} className="text-blue-400" />
              </div>
              <h2 className="text-2xl font-bold">Falta de Estrutura</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Muitos traders operam sem estrutura. Não têm processo definido, não revisam trades, não acompanham métricas. E depois se perguntam por que não conseguem ser consistentes.
            </p>

            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-5 text-center">
                <XCircle size={32} className="text-red-400 mx-auto mb-3" />
                <h3 className="font-semibold text-white mb-2">Sem Processo</h3>
                <p className="text-gray-500 text-xs">Cada dia é diferente. Não há rotina, não há checklist, não há padrão de execução.</p>
              </div>

              <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-5 text-center">
                <XCircle size={32} className="text-red-400 mx-auto mb-3" />
                <h3 className="font-semibold text-white mb-2">Sem Revisão</h3>
                <p className="text-gray-500 text-xs">Trades acontecem e são esquecidos. Não há momento de análise, reflexão ou aprendizado.</p>
              </div>

              <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-5 text-center">
                <XCircle size={32} className="text-red-400 mx-auto mb-3" />
                <h3 className="font-semibold text-white mb-2">Sem Dados</h3>
                <p className="text-gray-500 text-xs">Nenhuma métrica é acompanhada. O trader não sabe seu win rate, RR ou quais setups funcionam.</p>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Consistência não surge do nada. Ela é construída sobre uma base de estrutura, processo e dados. Sem essa base, você está tentando construir um prédio na areia.
            </p>
          </section>

          {/* Seção 7 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#00FF85]/10 flex items-center justify-center">
                <Gear size={24} className="text-[#00FF85]" />
              </div>
              <h2 className="text-2xl font-bold">Consistência Vem de Sistema</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              A boa notícia: consistência é construível. Não é talento, não é dom — é sistema. E sistemas podem ser criados, testados e melhorados.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-5">
                <h3 className="font-semibold text-[#00FF85] mb-2">Regras Claras</h3>
                <p className="text-gray-400 text-sm">Defina exatamente quando entrar, quando sair, quanto arriscar. Sem ambiguidade. Regras vagas geram execução inconsistente.</p>
              </div>

              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-5">
                <h3 className="font-semibold text-[#00FF85] mb-2">Repetição</h3>
                <p className="text-gray-400 text-sm">Faça a mesma coisa, da mesma forma, repetidamente. Consistência não vem de inspiração — vem de hábito treinado.</p>
              </div>

              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-5">
                <h3 className="font-semibold text-[#00FF85] mb-2">Feedback</h3>
                <p className="text-gray-400 text-sm">Revise cada trade. Identifique o que funcionou e o que não funcionou. Sem feedback, não há melhoria.</p>
              </div>

              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-5">
                <h3 className="font-semibold text-[#00FF85] mb-2">Análise</h3>
                <p className="text-gray-400 text-sm">Olhe para os números. Acompanhe métricas. Deixe os dados mostrarem a verdade sobre seu trading.</p>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Um sistema não elimina erros — mas reduz drasticamente sua frequência e permite que você aprenda com cada um deles.
            </p>
          </section>

          {/* Seção 8 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Target size={24} className="text-purple-400" />
              </div>
              <h2 className="text-2xl font-bold">De Trading Emocional para Trading Estruturado</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              A transformação fundamental é sair do modo reativo e entrar no modo planejado. De operador aleatório para trader estruturado.
            </p>

            <div className="grid sm:grid-cols-2 gap-6 mb-6">
              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-6">
                <h3 className="font-semibold text-red-400 mb-4">Trading Emocional</h3>
                <ul className="space-y-2 text-sm text-gray-400">
                  <li className="flex items-center gap-2">
                    <XCircle size={16} className="text-red-400" />
                    <span>Reativo ao mercado</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <XCircle size={16} className="text-red-400" />
                    <span>Decisões no momento</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <XCircle size={16} className="text-red-400" />
                    <span>Baseado em feeling</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <XCircle size={16} className="text-red-400" />
                    <span>Resultados aleatórios</span>
                  </li>
                </ul>
              </div>

              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-6">
                <h3 className="font-semibold text-[#00FF85] mb-4">Trading Estruturado</h3>
                <ul className="space-y-2 text-sm text-gray-400">
                  <li className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-[#00FF85]" />
                    <span>Planejado antecipadamente</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-[#00FF85]" />
                    <span>Decisões pré-definidas</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-[#00FF85]" />
                    <span>Baseado em dados</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-[#00FF85]" />
                    <span>Resultados consistentes</span>
                  </li>
                </ul>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              A mudança não acontece da noite para o dia. É um processo gradual de construção de hábitos, estruturas e mentalidade. Mas é possível — e é o que separa os 10% que ganham dos 90% que perdem.
            </p>
          </section>

          {/* Seção 9 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#00FF85]/10 flex items-center justify-center">
                <Repeat size={24} className="text-[#00FF85]" />
              </div>
              <h2 className="text-2xl font-bold">O Que Realmente Gera Consistência</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Depois de entender os obstáculos, vem a parte prática: o que fazer para construir consistência real no trading?
            </p>

            <div className="space-y-4 mb-6">
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#00FF85]/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#00FF85] font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-2">Análise de Trades</h3>
                    <p className="text-gray-400 text-sm">Revise cada trade sistematicamente. Não quando der vontade — sempre. O hábito de análise é a base de tudo.</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#00FF85]/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#00FF85] font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-2">Acompanhamento de Métricas</h3>
                    <p className="text-gray-400 text-sm">Win rate, RR médio, profit factor, drawdown. Conheça seus números. Eles contam a verdade que suas emoções escondem.</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#00FF85]/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#00FF85] font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-2">Revisão Constante</h3>
                    <p className="text-gray-400 text-sm">Semanalmente, olhe para trás. O que funcionou? O que não funcionou? O que precisa mudar? A revisão transforma dados em aprendizado.</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#00FF85]/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#00FF85] font-bold">4</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-2">Controle Emocional</h3>
                    <p className="text-gray-400 text-sm">Reconheça quando está emocional. Tenha regras para esses momentos (pause, reduza tamanho, não opere). Autoconhecimento é proteção.</p>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Nenhum desses elementos sozinho gera consistência. É a combinação deles, praticada ao longo do tempo, que transforma o trading.
            </p>
          </section>

          {/* GENI Mention */}
          <section className="mb-16">
            <div className="bg-gradient-to-r from-[#00FF85]/10 via-[#00FF85]/5 to-transparent border border-[#00FF85]/20 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-4">Construa Seu Sistema de Consistência</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                Ferramentas como o <strong className="text-[#00FF85]">GENI</strong> ajudam traders a criar um sistema estruturado de análise, facilitando a construção de consistência ao longo do tempo. Em vez de depender da memória e disciplina pura, você tem uma estrutura que trabalha a seu favor.
              </p>
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 text-[#00FF85] font-semibold hover:underline"
              >
                Experimentar GENI Grátis <ArrowRight size={18} />
              </Link>
            </div>
          </section>

          {/* CTA Final */}
          <section className="mb-16">
            <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Consistência é Construída, Não Encontrada</h2>
              <p className="text-gray-400 mb-6 max-w-2xl mx-auto">
                Consistência no trading não vem de tentar mais, mas de ter um sistema que permita melhorar continuamente. Pare de buscar o setup perfeito e comece a construir o processo perfeito.
              </p>
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 bg-[#00FF85] text-black px-8 py-3 rounded-lg font-semibold hover:bg-[#00DD70] transition-colors"
              >
                Começar Grátis <ArrowRight size={20} />
              </Link>
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-8">Perguntas Frequentes</h2>

            <div className="space-y-4">
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">Como ser consistente no trading?</h3>
                <p className="text-gray-400 text-sm">Consistência vem de sistema: regras claras, registro de trades, análise regular, acompanhamento de métricas e controle emocional. Não existe atalho — é trabalho estruturado ao longo do tempo.</p>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">Por que é difícil manter disciplina no trading?</h3>
                <p className="text-gray-400 text-sm">Porque o mercado ativa emoções intensas (medo, ganância, ansiedade) e o feedback é inconsistente (trades ruins podem ganhar, trades bons podem perder). O cérebro fica confuso e a disciplina quebra sob pressão.</p>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">Psicologia influencia tanto assim no trading?</h3>
                <p className="text-gray-400 text-sm">Sim. Estima-se que 80% do sucesso no trading depende de psicologia e disciplina. Você pode ter o melhor sistema técnico do mundo, mas se não conseguir executá-lo consistentemente, não adianta.</p>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">Qual o maior erro dos traders?</h3>
                <p className="text-gray-400 text-sm">Operar sem estrutura. Sem registro de trades, sem análise, sem métricas. Isso impede qualquer evolução real porque o trader não tem feedback objetivo para melhorar.</p>
              </div>
            </div>
          </section>

          {/* Related Articles */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Artigos Relacionados</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link
                to="/pt/por-que-traders-nao-evoluem"
                className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 hover:border-[#00FF85]/50 transition-colors group"
              >
                <h3 className="font-semibold text-white mb-2 group-hover:text-[#00FF85] transition-colors">Por Que Traders Não Evoluem</h3>
                <p className="text-gray-500 text-sm">Entenda os principais erros comportamentais que impedem a evolução no trading.</p>
              </Link>

              <Link
                to="/pt/como-analisar-trades"
                className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 hover:border-[#00FF85]/50 transition-colors group"
              >
                <h3 className="font-semibold text-white mb-2 group-hover:text-[#00FF85] transition-colors">Como Analisar Seus Trades</h3>
                <p className="text-gray-500 text-sm">Guia prático para analisar trades de forma estruturada.</p>
              </Link>

              <Link
                to="/pt/diario-de-trades-automatico"
                className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 hover:border-[#00FF85]/50 transition-colors group"
              >
                <h3 className="font-semibold text-white mb-2 group-hover:text-[#00FF85] transition-colors">Diário de Trades Automático</h3>
                <p className="text-gray-500 text-sm">Como automatizar o registro dos seus trades e economizar tempo.</p>
              </Link>

              <Link
                to="/pt/diario-de-trades-cripto"
                className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 hover:border-[#00FF85]/50 transition-colors group"
              >
                <h3 className="font-semibold text-white mb-2 group-hover:text-[#00FF85] transition-colors">Diário de Trades para Cripto</h3>
                <p className="text-gray-500 text-sm">Especificidades de um diário de trading para criptomoedas.</p>
              </Link>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-800/50 py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2">
              <img src="/geni-logo.png" alt="GENI" className="h-6" />
              <span className="font-semibold">GENI</span>
            </Link>
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <Link to="/about" className="hover:text-white transition-colors">Sobre</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Termos</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacidade</Link>
            </div>
            <p className="text-sm text-gray-600">
              © 2025 GENI. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PorQueConsistenciaEDificil;
